import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/shared/SEO';
import { useI18n } from '../i18n/useI18n';

const COMPANY = {
  name: 'PT. Rekayasa Analisa Digital',
  address: 'Jalan Margonda Raya No.328 RT.00 RW.00, Kemiri Muka, Beji, Kota Depok, Jawa Barat',
  email: 'sales@rekayasa.io',
};

const lastUpdatedISO = '2026-02-04';

const formatDate = (lang: 'en' | 'id', iso: string) => {
  const d = new Date(`${iso}T00:00:00Z`);
  return new Intl.DateTimeFormat(lang === 'id' ? 'id-ID' : 'en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(d);
};

export default function PrivacyPolicyPage() {
  const { lang } = useI18n();

  const title = lang === 'id' ? 'Kebijakan Privasi' : 'Privacy Policy';
  const description =
    lang === 'id'
      ? 'Kebijakan privasi untuk situs Rekayasa / ERICA.'
      : 'Privacy policy for the Rekayasa / ERICA website.';

  return (
    <div className="bg-white">
      <SEO title={`${title} | ERICA by Rekayasa`} description={description} lang={lang} />

      <div className="mx-auto max-w-3xl px-4 pb-20 pt-28 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
            {title}
          </h1>
          <Link to="/" className="text-sm font-semibold text-slate-700 hover:text-slate-900">
            {lang === 'id' ? 'Kembali' : 'Back'}
          </Link>
        </div>

        <p className="mt-4 text-sm text-slate-600">
          {lang === 'id' ? 'Terakhir diperbarui: ' : 'Last updated: '}
          <span className="font-medium text-slate-800">{formatDate(lang, lastUpdatedISO)}</span>
        </p>

        <div className="mt-10 space-y-10 text-slate-700">
          <section>
            <h2 className="text-lg font-semibold text-slate-900">
              {lang === 'id' ? 'Ringkasan' : 'Summary'}
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              {lang === 'id'
                ? 'Dokumen ini menjelaskan bagaimana kami mengumpulkan, menggunakan, dan melindungi data pribadi ketika Anda mengunjungi situs ini atau mengajukan permintaan demo ERICA.'
                : 'This document explains how we collect, use, and protect personal data when you visit this website or request an ERICA demo.'}
            </p>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              {lang === 'id'
                ? 'Ini bukan nasihat hukum. Jika Anda membutuhkan kebijakan khusus untuk kebutuhan compliance perusahaan Anda, mohon konsultasikan dengan penasihat hukum.'
                : 'This is not legal advice. If you need a policy tailored to your compliance requirements, please consult legal counsel.'}
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900">
              {lang === 'id' ? 'Data yang kami kumpulkan' : 'Data we collect'}
            </h2>
            <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-slate-600">
              <li>
                {lang === 'id'
                  ? 'Data kontak yang Anda kirim melalui form “Minta demo” (mis. nama, email kantor, perusahaan, peran/fungsi, dan pesan).'
                  : 'Contact data you submit via the “Request demo” form (e.g., name, work email, company, role/function, and message).'}
              </li>
              <li>
                {lang === 'id'
                  ? 'Preferensi bahasa (EN/ID) dan status persetujuan cookies yang disimpan di browser Anda.'
                  : 'Language preference (EN/ID) and cookie-consent status stored in your browser.'}
              </li>
              <li>
                {lang === 'id'
                  ? 'Data teknis standar saat Anda mengakses situs (mis. log server/hosting), sejauh disediakan oleh penyedia hosting.'
                  : 'Standard technical data when you access the site (e.g., hosting/server logs), as provided by our hosting provider.'}
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900">
              {lang === 'id' ? 'Cara kami menggunakan data' : 'How we use data'}
            </h2>
            <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-slate-600">
              <li>
                {lang === 'id'
                  ? 'Menindaklanjuti permintaan demo, menghubungi Anda, dan menjadwalkan sesi.'
                  : 'To follow up on demo requests, contact you, and schedule sessions.'}
              </li>
              <li>
                {lang === 'id'
                  ? 'Mengoperasikan dan meningkatkan kualitas situs (mis. memastikan fitur berjalan dengan baik).'
                  : 'To operate and improve the website (e.g., ensuring features work properly).'}
              </li>
              <li>
                {lang === 'id'
                  ? 'Kepatuhan dan keamanan (mis. pencegahan penyalahgunaan).'
                  : 'Compliance and security (e.g., abuse prevention).'}
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900">
              {lang === 'id' ? 'Pihak ketiga & pemrosesan' : 'Third parties & processing'}
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              {lang === 'id'
                ? 'Permintaan demo dapat dikirim melalui layanan form pihak ketiga (mis. Formspree/Tally) sesuai konfigurasi. Layanan tersebut akan memproses data yang Anda kirim untuk meneruskan ke kami.'
                : 'Demo requests may be submitted through a third-party form service (e.g., Formspree/Tally) depending on configuration. That service processes the data you submit to deliver it to us.'}
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900">
              {lang === 'id' ? 'Penyimpanan & retensi' : 'Storage & retention'}
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              {lang === 'id'
                ? 'Kami menyimpan data selama diperlukan untuk menindaklanjuti permintaan Anda, memenuhi kewajiban hukum, atau tujuan operasional yang sah. Retensi dapat disesuaikan sesuai kebutuhan enterprise.'
                : 'We retain data as needed to respond to your request, meet legal obligations, or for legitimate operational purposes. Retention can be aligned with enterprise requirements.'}
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900">
              {lang === 'id' ? 'Keamanan' : 'Security'}
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              {lang === 'id'
                ? 'Kami menerapkan langkah-langkah keamanan yang wajar untuk melindungi data. Namun, tidak ada sistem yang sepenuhnya bebas risiko.'
                : 'We implement reasonable security measures to protect data. However, no system is completely risk-free.'}
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900">
              {lang === 'id' ? 'Hak subjek data (UU PDP)' : 'Data subject rights (PDP Law)'}
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              {lang === 'id'
                ? 'Kami berupaya menyelaraskan pemrosesan data dengan UU PDP. Anda dapat menghubungi kami untuk meminta akses, koreksi, atau penghapusan data, sesuai ketentuan yang berlaku.'
                : 'We aim to align data processing with Indonesia’s PDP Law. You can contact us to request access, correction, or deletion of your data, subject to applicable rules.'}
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900">
              {lang === 'id' ? 'Kontak & informasi perusahaan' : 'Contact & company information'}
            </h2>
            <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-700">
              <div className="font-semibold text-slate-900">{COMPANY.name}</div>
              <div className="mt-2 space-y-1">
                <div>
                  <span className="font-semibold">{lang === 'id' ? 'Alamat' : 'Address'}:</span>{' '}
                  {COMPANY.address}
                </div>
                <div>
                  <span className="font-semibold">{lang === 'id' ? 'Email' : 'Email'}:</span>{' '}
                  <a className="underline hover:text-slate-900" href={`mailto:${COMPANY.email}`}>
                    {COMPANY.email}
                  </a>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
